import React from 'react'
import { useMapEditorStore } from './mapEditorStore'
import {
  EDITOR_ENEMY_OPTIONS,
  formatComposition,
  summarizeMapWaves,
  type EnemyFamilyId
} from './waveHelpers'

export const WaveEditorPanel: React.FC = () => {
  const map = useMapEditorStore((s) => s.map)
  const addWave = useMapEditorStore((s) => s.addWave)
  const deleteWave = useMapEditorStore((s) => s.deleteWave)
  const moveWave = useMapEditorStore((s) => s.moveWave)
  const duplicateWaveAt = useMapEditorStore((s) => s.duplicateWaveAt)
  const updateWave = useMapEditorStore((s) => s.updateWave)
  const addGroup = useMapEditorStore((s) => s.addGroup)
  const deleteGroup = useMapEditorStore((s) => s.deleteGroup)
  const moveGroup = useMapEditorStore((s) => s.moveGroup)
  const updateGroup = useMapEditorStore((s) => s.updateGroup)

  const summary = summarizeMapWaves(map)

  return (
    <div className="wave-editor" data-testid="wave-editor">
      <div className="wave-editor__totals" data-testid="wave-totals">
        <p>
          Total {summary.totalEnemies} enemies ·{' '}
          {EDITOR_ENEMY_OPTIONS.map((opt) => `${summary.composition[opt.id] ?? 0} ${opt.label}`).join(
            ' · '
          )}
        </p>
        <p>Completion rewards sum {summary.totalRewards}</p>
      </div>

      <button
        type="button"
        className="map-editor__btn"
        data-testid="add-wave"
        onClick={(e) => {
          e.preventDefault()
          addWave()
        }}
      >
        ADD WAVE
      </button>

      <ul className="wave-editor__list">
        {map.waves.map((wave, waveIndex) => {
          const waveSummary = summary.waves[waveIndex]
          return (
            <li key={`${wave.id}-${waveIndex}`} className="wave-editor__wave" data-testid={`wave-${waveIndex}`}>
              <div className="wave-editor__wave-head">
                <strong>
                  {waveSummary.label} · {formatComposition(waveSummary.composition)}
                </strong>
                <span>
                  {waveSummary.composition.total} enemies · Reward {wave.completionReward}
                </span>
              </div>

              <div className="wave-editor__wave-meta">
                <label className="map-editor__field">
                  <span>Wave ID</span>
                  <input
                    value={wave.id}
                    data-testid={`wave-id-${waveIndex}`}
                    onChange={(e) => updateWave(waveIndex, { id: e.target.value })}
                  />
                </label>
                <label className="map-editor__field map-editor__field--narrow">
                  <span>Reward</span>
                  <input
                    type="number"
                    min={0}
                    value={wave.completionReward}
                    data-testid={`wave-reward-${waveIndex}`}
                    onChange={(e) => {
                      const completionReward = Number(e.target.value)
                      if (Number.isInteger(completionReward)) {
                        updateWave(waveIndex, { completionReward })
                      }
                    }}
                  />
                </label>
              </div>

              <div className="wave-editor__wave-actions">
                <button type="button" className="map-editor__btn" disabled={waveIndex === 0} onClick={() => moveWave(waveIndex, -1)}>
                  MOVE UP
                </button>
                <button
                  type="button"
                  className="map-editor__btn"
                  disabled={waveIndex >= map.waves.length - 1}
                  onClick={() => moveWave(waveIndex, 1)}
                >
                  MOVE DOWN
                </button>
                <button type="button" className="map-editor__btn" onClick={() => duplicateWaveAt(waveIndex)}>
                  DUPLICATE
                </button>
                <button
                  type="button"
                  className="map-editor__btn"
                  data-testid={`delete-wave-${waveIndex}`}
                  onClick={() => deleteWave(waveIndex)}
                >
                  DELETE WAVE
                </button>
              </div>

              <ul className="wave-editor__groups">
                {wave.groups.map((group, groupIndex) => (
                  <li key={groupIndex} className="wave-editor__group" data-testid={`wave-${waveIndex}-group-${groupIndex}`}>
                    <label className="map-editor__field">
                      <span>Enemy</span>
                      <select
                        value={group.enemy}
                        data-testid={`enemy-${waveIndex}-${groupIndex}`}
                        onChange={(e) =>
                          updateGroup(waveIndex, groupIndex, {
                            enemy: e.target.value as EnemyFamilyId
                          })
                        }
                      >
                        {EDITOR_ENEMY_OPTIONS.map((opt) => (
                          <option key={opt.id} value={opt.id}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="map-editor__field map-editor__field--narrow">
                      <span>Count</span>
                      <input
                        type="number"
                        min={1}
                        value={group.count}
                        onChange={(e) => {
                          const count = Number(e.target.value)
                          if (Number.isInteger(count)) updateGroup(waveIndex, groupIndex, { count })
                        }}
                      />
                    </label>
                    <label className="map-editor__field map-editor__field--narrow">
                      <span>Interval</span>
                      <input
                        type="number"
                        min={0}
                        step={0.05}
                        value={group.interval}
                        onChange={(e) => {
                          const interval = Number(e.target.value)
                          if (Number.isFinite(interval)) updateGroup(waveIndex, groupIndex, { interval })
                        }}
                      />
                    </label>
                    <label className="map-editor__field map-editor__field--narrow">
                      <span>Delay</span>
                      <input
                        type="number"
                        min={0}
                        step={0.05}
                        value={group.delay}
                        onChange={(e) => {
                          const delay = Number(e.target.value)
                          if (Number.isFinite(delay)) updateGroup(waveIndex, groupIndex, { delay })
                        }}
                      />
                    </label>
                    <div className="wave-editor__group-actions">
                      <button
                        type="button"
                        className="map-editor__btn"
                        disabled={groupIndex === 0}
                        onClick={() => moveGroup(waveIndex, groupIndex, -1)}
                      >
                        UP
                      </button>
                      <button
                        type="button"
                        className="map-editor__btn"
                        disabled={groupIndex >= wave.groups.length - 1}
                        onClick={() => moveGroup(waveIndex, groupIndex, 1)}
                      >
                        DOWN
                      </button>
                      <button
                        type="button"
                        className="map-editor__btn"
                        onClick={() => deleteGroup(waveIndex, groupIndex)}
                      >
                        DELETE
                      </button>
                    </div>
                  </li>
                ))}
              </ul>

              <button
                type="button"
                className="map-editor__btn"
                data-testid={`add-group-${waveIndex}`}
                onClick={() => addGroup(waveIndex)}
              >
                ADD GROUP
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
